import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Gauge, Droplet, Zap, Wrench, Activity, Clock, Info, ExternalLink } from 'lucide-react';

export const VehicleSpecDetail = ({ spec, onVerify, onClose }) => {
  const specs = spec.specifications || {};
  const completeness = (spec.completeness_score || 0) * 100;
  const isVerified = spec.verification_status === 'verified';

  // Helper function to format values
  const formatValue = (value) => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'boolean') return value ? 'Ja' : 'Nein';
    if (typeof value === 'number') return value.toString();
    if (typeof value === 'string') return value;
    if (Array.isArray(value)) {
      return value.map(v => typeof v === 'object' ? JSON.stringify(v) : v).join(', ');
    }
    return String(value);
  };

  // Helper function to render nested objects nicely
  const renderNestedObject = (obj, depth = 0) => {
    if (!obj || typeof obj !== 'object') return <span>{formatValue(obj)}</span>;
    
    if (Array.isArray(obj)) {
      return (
        <ul className="space-y-1 pl-2">
          {obj.map((item, idx) => (
            <li key={idx} className="text-sm" style={{ color: '#E6EDF3' }}>
              • {typeof item === 'object' ? renderNestedObject(item, depth + 1) : formatValue(item)}
            </li>
          ))}
        </ul>
      );
    }

    return (
      <div className={`space-y-2 ${depth > 0 ? 'pl-3 border-l-2 mt-1' : ''}`} style={{ borderColor: '#1F2937' }}>
        {Object.entries(obj).map(([key, value]) => {
          if (value === null || value === undefined) return null;
          
          const label = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
          
          return (
            <div key={key}>
              <span className="text-xs font-medium" style={{ color: '#94A3B8' }}>
                {label}:
              </span>{' '}
              {typeof value === 'object' ? (
                renderNestedObject(value, depth + 1)
              ) : (
                <span className="text-sm" style={{ color: '#E6EDF3' }}>
                  {formatValue(value)}
                </span>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderSection = (title, icon, data, keyLabels) => {
    if (!data || Object.keys(data).length === 0) return null;

    return (
      <Card className="mb-4" style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937' }}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg" style={{ color: '#E6EDF3' }}>
            {icon}
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {Object.entries(data).map(([key, value]) => {
              if (value === null || value === undefined || (typeof value === 'object' && Object.keys(value).length === 0)) return null;
              
              const label = keyLabels[key] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
              
              return (
                <div key={key} className="p-3 rounded" style={{ backgroundColor: '#0F141A' }}>
                  <p className="text-xs font-semibold mb-1" style={{ color: '#7CD4FD' }}>
                    {label}
                  </p>
                  {typeof value === 'object' ? (
                    renderNestedObject(value)
                  ) : (
                    <p className="text-sm" style={{ color: '#E6EDF3' }}>
                      {formatValue(value)}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderSensors = (sensors) => {
    if (!sensors || sensors.length === 0) return null;

    return (
      <Card className="mb-4" style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937' }}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg" style={{ color: '#E6EDF3' }}>
            <Activity size={20} />
            Sensoren & Messw erte
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {sensors.map((sensor, idx) => (
              <div key={idx} className="p-4 rounded" style={{ backgroundColor: '#0F141A', borderLeft: '3px solid #1EA0E6' }}>
                <h4 className="font-semibold mb-2" style={{ color: '#E6EDF3' }}>
                  {sensor.name}
                </h4>
                {sensor.location && (
                  <p className="text-sm mb-2" style={{ color: '#7CD4FD' }}>
                    📍 {sensor.location}
                  </p>
                )}
                <div className="space-y-2">
                  {sensor.resistance_values && (
                    <div>
                      <p className="font-semibold mb-1 text-xs" style={{ color: '#94A3B8' }}>Widerstand:</p>
                      <div className="space-y-1">
                        {typeof sensor.resistance_values === 'object' ? (
                          Object.entries(sensor.resistance_values).map(([temp, value]) => (
                            <div key={temp} className="flex justify-between text-sm px-2 py-1 rounded" style={{ backgroundColor: '#1F2937' }}>
                              <span style={{ color: '#7CD4FD' }}>{temp}:</span>
                              <span style={{ color: '#E6EDF3' }}>{value}</span>
                            </div>
                          ))
                        ) : (
                          <p className="text-sm" style={{ color: '#E6EDF3' }}>{sensor.resistance_values}</p>
                        )}
                      </div>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-3">
                    {sensor.voltage_range && (
                      <div>
                        <p className="font-semibold mb-1 text-xs" style={{ color: '#94A3B8' }}>Spannung:</p>
                        <p className="text-sm" style={{ color: '#E6EDF3' }}>{sensor.voltage_range}</p>
                      </div>
                    )}
                    {sensor.signal_type && (
                      <div className="text-right">
                        <p className="font-semibold mb-1 text-xs" style={{ color: '#94A3B8' }}>Signal:</p>
                        <p className="text-sm" style={{ color: '#E6EDF3' }}>{sensor.signal_type}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderCustomData = (customData) => {
    if (!customData || Object.keys(customData).length === 0) return null;

    return (
      <Card className="mb-4" style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937' }}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg" style={{ color: '#E6EDF3' }}>
            <Info size={20} />
            Weitere technische Daten
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(customData).map(([key, value]) => {
              if (value === null || value === undefined) return null;
              
              const label = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
              
              return (
                <div key={key} className="p-4 rounded" style={{ backgroundColor: '#0F141A' }}>
                  <p className="text-sm font-semibold mb-2" style={{ color: '#7CD4FD' }}>
                    {label}
                  </p>
                  <div className="text-sm" style={{ color: '#E6EDF3' }}>
                    {typeof value === 'object' ? renderNestedObject(value) : formatValue(value)}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="max-h-[80vh] overflow-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold" style={{ color: '#E6EDF3' }}>
              {spec.brand} {spec.model}
            </h2>
            {spec.engine && (
              <p className="text-sm mt-1" style={{ color: '#94A3B8' }}>
                Motor: {spec.engine} {spec.engine_code && `(${spec.engine_code})`}
              </p>
            )}
            {spec.years && spec.years.length > 0 && (
              <p className="text-xs" style={{ color: '#94A3B8' }}>
                Baujahre: {spec.years.join(', ')}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Badge variant={isVerified ? 'success' : 'warning'}>
              {isVerified ? '✅ Verifiziert' : '⚠️ Nicht verifiziert'}
            </Badge>
            {!isVerified && onVerify && (
              <Button
                onClick={() => onVerify(spec.id)}
                size="sm"
                style={{ backgroundColor: '#22C55E', color: '#FFFFFF' }}
              >
                Verifizieren
              </Button>
            )}
          </div>
        </div>

        {/* Completeness Bar */}
        <div className="flex items-center gap-3 mb-4">
          <div className="flex-1 bg-gray-700 rounded-full h-2">
            <div
              className="h-2 rounded-full"
              style={{
                width: `${completeness}%`,
                backgroundColor: completeness >= 80 ? '#22C55E' : completeness >= 50 ? '#F59E0B' : '#EF4444'
              }}
            />
          </div>
          <span className="text-sm" style={{ color: '#94A3B8' }}>
            {completeness.toFixed(0)}% vollständig
          </span>
        </div>
      </div>

      {/* Engine */}
      {renderSection('Motor-Spezifikationen', <Gauge size={20} />, specs.engine, {
        displacement: 'Hubraum',
        power: 'Leistung',
        torque: 'Drehmoment',
        cylinder_count: 'Zylinderzahl',
        cylinder_arrangement: 'Zylinderanordnung',
        valves_per_cylinder: 'Ventile pro Zylinder',
        fuel_type: 'Kraftstoffart',
        turbocharger: 'Turbolader',
        intercooler: 'Ladeluftkühler',
        valve_timing_system: 'Ventilsteuerung',
        emission_standard: 'Abgasnorm',
        engine_weight: 'Motorgewicht',
        compression_ratio: 'Verdichtung',
        oil_capacity: 'Motoröl-Kapazität',
        oil_type: 'Motoröl-Typ',
        coolant_capacity: 'Kühlmittel-Kapazität',
        coolant_type: 'Kühlmittel-Typ',
        spark_plugs: 'Zündkerzen',
        spark_plug_gap: 'Zündkerzen-Abstand',
        timing_chain_stretch_limit: 'Steuerkette Längung max.'
      })}

      {/* Fuel System */}
      {renderSection('Kraftstoffsystem', <Droplet size={20} />, specs.fuel_system, {
        fuel_tank_capacity: 'Tankinhalt',
        fuel_consumption_city: 'Verbrauch Stadt',
        fuel_consumption_highway: 'Verbrauch Autobahn',
        fuel_consumption_combined: 'Verbrauch kombiniert',
        co2_emissions: 'CO2-Emissionen',
        injection_type: 'Einspritzsystem',
        rail_pressure_idle: 'Rail-Druck Leerlauf',
        rail_pressure_load: 'Rail-Druck Last',
        fuel_pressure: 'Kraftstoffdruck',
        injector_resistance: 'Injektor-Widerstand',
        fuel_pump_pressure: 'Kraftstoffpumpen-Druck'
      })}

      {/* Air Conditioning */}
      {renderSection('Klimaanlage', <Zap size={20} />, specs.air_conditioning, {
        refrigerant_type: 'Kältemittel-Typ',
        refrigerant_amount: 'Kältemittel-Menge',
        compressor_oil_type: 'Kompressor-Öl Typ',
        compressor_oil_amount: 'Kompressor-Öl Menge'
      })}

      {/* Torque Specs */}
      {renderSection('Drehmoment-Spezifikationen', <Wrench size={20} />, specs.torque_specs, {
        cylinder_head_bolts: 'Zylinderkopf-Schrauben',
        oil_drain_plug: 'Ölablassschraube',
        wheel_bolts: 'Radschrauben',
        spark_plugs: 'Zündkerzen',
        oil_filter: 'Ölfilter',
        transmission_drain_plug: 'Getriebe-Ablassschraube',
        custom: 'Weitere Drehmomente'
      })}

      {/* Sensors */}
      {renderSensors(specs.sensors)}

      {/* Electrical System */}
      {renderSection('Elektrisches System', <Zap size={20} />, specs.electrical_system, {
        battery_voltage: 'Batteriespannung',
        battery_capacity: 'Batteriekapazität',
        battery_type: 'Batterietyp',
        alternator_output: 'Lichtmaschinen-Leistung',
        starter_motor_power: 'Anlasser-Leistung',
        main_fuse_rating: 'Hauptsicherung'
      })}

      {/* Transmission */}
      {renderSection('Getriebe', <Wrench size={20} />, specs.transmission, {
        transmission_type: 'Getriebetyp',
        gear_count: 'Anzahl Gänge',
        transmission_oil_capacity: 'Getriebeöl-Kapazität',
        transmission_oil_type: 'Getriebeöl-Typ',
        clutch_type: 'Kupplungstyp',
        final_drive_ratio: 'Achsübersetzung'
      })}

      {/* Braking System */}
      {renderSection('Bremssystem', <Activity size={20} />, specs.braking_system, {
        front_brake_type: 'Bremsen vorne',
        rear_brake_type: 'Bremsen hinten',
        front_disc_diameter: 'Scheibendurchmesser vorne',
        rear_disc_diameter: 'Scheibendurchmesser hinten',
        brake_fluid_type: 'Bremsflüssigkeit',
        abs: 'ABS',
        esp: 'ESP'
      })}

      {/* Suspension */}
      {renderSection('Fahrwerk', <Activity size={20} />, specs.suspension, {
        front_suspension: 'Federung vorne',
        rear_suspension: 'Federung hinten',
        shock_absorber_type: 'Stoßdämpfertyp',
        spring_rate_front: 'Federrate vorne',
        spring_rate_rear: 'Federrate hinten'
      })}

      {/* Common Repairs */}
      {specs.common_repairs && (specs.common_repairs.common_issues?.length > 0 || specs.common_repairs.typical_wear_parts?.length > 0) && (
        <Card className="mb-4" style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937' }}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg" style={{ color: '#E6EDF3' }}>
              <Info size={20} />
              Häufige Reparaturen & Bekannte Probleme
            </CardTitle>
          </CardHeader>
          <CardContent>
            {specs.common_repairs.common_issues && specs.common_repairs.common_issues.length > 0 && (
              <div className="mb-4">
                <p className="font-semibold mb-2 text-sm" style={{ color: '#F59E0B' }}>
                  ⚠️ Bekannte Probleme:
                </p>
                <ul className="space-y-1">
                  {specs.common_repairs.common_issues.map((issue, idx) => (
                    <li key={idx} className="text-sm p-2 rounded" style={{ backgroundColor: '#0F141A', color: '#E6EDF3' }}>
                      • {issue}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {specs.common_repairs.typical_wear_parts && specs.common_repairs.typical_wear_parts.length > 0 && (
              <div className="mb-4">
                <p className="font-semibold mb-2 text-sm" style={{ color: '#7CD4FD' }}>
                  🔧 Typische Verschleißteile:
                </p>
                <div className="flex flex-wrap gap-2">
                  {specs.common_repairs.typical_wear_parts.map((part, idx) => (
                    <Badge key={idx} variant="secondary">
                      {part}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            {specs.common_repairs.maintenance_tips && specs.common_repairs.maintenance_tips.length > 0 && (
              <div>
                <p className="font-semibold mb-2 text-sm" style={{ color: '#10B981' }}>
                  💡 Wartungstipps:
                </p>
                <ul className="space-y-1">
                  {specs.common_repairs.maintenance_tips.map((tip, idx) => (
                    <li key={idx} className="text-sm" style={{ color: '#94A3B8' }}>
                      • {tip}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Service Intervals */}
      {renderSection('Service-Intervalle', <Clock size={20} />, specs.service_intervals, {
        oil_change: 'Ölwechsel',
        air_filter: 'Luftfilter',
        fuel_filter: 'Kraftstofffilter',
        spark_plugs: 'Zündkerzen',
        timing_belt: 'Zahnriemen',
        timing_chain_inspection: 'Steuerkette Inspektion',
        brake_fluid: 'Bremsflüssigkeit',
        coolant: 'Kühlmittel',
        transmission_oil: 'Getriebeöl',
        brake_pads_front: 'Bremsbeläge vorne',
        brake_pads_rear: 'Bremsbeläge hinten'
      })}

      {/* Custom Data */}
      {renderCustomData(specs.custom_data)}

      {/* Sources */}
      {spec.sources && spec.sources.length > 0 && (
        <Card style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937' }}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg" style={{ color: '#E6EDF3' }}>
              <ExternalLink size={20} />
              Quellen ({spec.sources.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {spec.sources.map((source, idx) => {
                const reliabilityColor = source.reliability === 'high' ? '#22C55E' : 
                                        source.reliability === 'medium' ? '#F59E0B' : '#EF4444';
                return (
                  <div key={idx} className="flex items-start gap-2 text-sm">
                    <Badge style={{ backgroundColor: reliabilityColor + '30', color: reliabilityColor }}>
                      {source.reliability}
                    </Badge>
                    <div className="flex-1">
                      <a 
                        href={source.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="hover:underline"
                        style={{ color: '#1EA0E6' }}
                      >
                        {source.title}
                      </a>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};